/**
 * Day choices for the "Jetzt passend" home section.
 *
 * The section answers "what fits now", so the offer stays short: during the
 * week today, tomorrow and the coming weekend days; on the weekend itself only
 * the day ahead. A day is identified by its date, which keeps the picked chip
 * stable when the labels switch language.
 */
const WEEKDAYS_DE = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
const WEEKDAYS_EN = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Weekday slugs used by the Ausflugsideen day filter, indexed by Date.getDay(). */
const WEEKDAY_KEYS = ['sonntag', 'montag', 'dienstag', 'mittwoch', 'donnerstag', 'freitag', 'samstag'];

/** Days offered, as offsets in days from today (0 = today). */
const OFFSETS_BY_WEEKDAY = {
  0: [0, 1],          // Sonntag:  heute, morgen (Montag)
  1: [0, 1, 5, 6],    // Montag:   heute, morgen, Samstag, Sonntag
  2: [0, 1, 4, 5],
  3: [0, 1, 3, 4],
  4: [0, 1, 2, 3],
  5: [0, 1, 2],       // Freitag:  heute, Samstag, Sonntag
  6: [0, 1],          // Samstag:  heute, Sonntag
};

const pad = (n) => String(n).padStart(2, '0');

/** Local date as YYYY-MM-DD (never UTC — the day must match the user's clock). */
export function isoDate(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function buildDayOptions(now = new Date(), language = 'de') {
  const weekday = now.getDay();
  const weekdays = language === 'en' ? WEEKDAYS_EN : WEEKDAYS_DE;

  return OFFSETS_BY_WEEKDAY[weekday].map((offset) => {
    const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
    const weekdayIndex = date.getDay();
    // "Morgen" only carries the plain weekdays; on the way into the weekend the
    // chip already names the day itself ("Samstag").
    const isTomorrow = offset === 1 && weekday >= 1 && weekday <= 4;
    const label = offset === 0
      ? (language === 'en' ? 'Today' : 'Heute')
      : isTomorrow
        ? (language === 'en' ? 'Tomorrow' : 'Morgen')
        : weekdays[weekdayIndex];

    return { key: isoDate(date), offset, weekdayIndex, date, label, isToday: offset === 0, isTomorrow };
  });
}

/** After 19:00 the section looks at tomorrow instead of today. */
export function defaultDayOffset(now = new Date()) {
  return now.getHours() >= 19 ? 1 : 0;
}

export function dayTitle(option, language = 'de') {
  if (!option || option.isToday) return language === 'en' ? "Today's family trip picks" : 'Jetzt passende Ausflugsideen';
  if (option.isTomorrow) return language === 'en' ? "Tomorrow's family trip picks" : 'Morgen passende Familienausflüge';
  return language === 'en' ? `${option.label} family trip picks` : `Am ${option.label} passende Ausflugsziele`;
}

/** "10:00" → "10", "08:30" → "8:30" — the badge names hours the way people say them. */
function shortTime(time) {
  const [hour, minute] = String(time).split(':');
  return minute === '00' ? String(Number(hour)) : `${Number(hour)}:${minute}`;
}

/** Badge text on a card: the picked day with that day's opening hours. */
export function dayBadgeLabel(option, hours, language = 'de') {
  const suffix = language === 'en' ? '' : ' Uhr';
  return `${option.label} ${shortTime(hours.openTime)}–${shortTime(hours.closeTime)}${suffix}`;
}

/** Slug for the Ausflugsideen day filter (e.g. 6 → "samstag"). */
export function weekdayKey(weekdayIndex) {
  return WEEKDAY_KEYS[weekdayIndex];
}